import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import { useNavigate } from 'react-router-dom';
import API_CONFIG, { buildApiUrl, buildApiUrlWithParams } from '../config/api';

const Class = () => {
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userGroups, setUserGroups] = useState([]);
    const [editingClass, setEditingClass] = useState(null);
    const [ranks, setRanks] = useState([]);
    const [newClass, setNewClass] = useState({
        class_name: '',
        active: true,
        day_of_week: 1,
        start_time: '',
        min_rank: 1,
        max_rank: 7,
        min_age: 3,
        max_age: 99
    });
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem("token"); // Retrieve token
        if (!token) {
            navigate('/login');
            return;
        }

        const decoded = JSON.parse(atob(token.split(".")[1]));
        setUserGroups(decoded["cognito:groups"]);

        fetchRanks();
        fetchClasses();
    }, [navigate]);

    const fetchRanks = async () => {
        try {
            const token = localStorage.getItem("token"); // Retrieve token
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.RANKS), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setRanks(data);
        } catch (error) {
            console.error('Error fetching ranks:', error);
        }
    };

    const fetchClasses = async () => {
        try {
            const token = localStorage.getItem("token"); // Retrieve token
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.CLASSES), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            console.log('Fetched classes:', data);
            setClasses(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching classes:', error);
            setLoading(false);
        }
    };

    const handleEdit = (classItem) => {
        setEditingClass({ ...classItem });
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem("token"); // Retrieve token
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.CLASSES), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editingClass)
            });

            if (response.ok) {
                setEditingClass(null);
                fetchClasses();
            }
        } catch (error) {
            console.error('Error updating class:', error);
        }
    };

    const handleDelete = async (classId) => {
        if (window.confirm('Are you sure you want to delete this class?')) {
            try {
                const token = localStorage.getItem("token"); // Retrieve token
                const response = await fetch(buildApiUrlWithParams(API_CONFIG.ENDPOINTS.CLASS_DETAIL, classId), {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    fetchClasses();
                }
            } catch (error) {
                console.error('Error deleting class:', error);
            }
        }
    };

    const handleAdd = async () => {
        try {
            const token = localStorage.getItem("token"); // Retrieve token
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.CLASSES), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newClass)
            });

            if (response.ok) {
                setNewClass({
                    class_name: '',
                    active: true,
                    day_of_week: 1,
                    start_time: '',
                    min_rank: 1,
                    max_rank: 7,
                    min_age: 3,
                    max_age: 99
                });
                fetchClasses();
            }
        } catch (error) {
            console.error('Error adding class:', error);
        }
    };

    const isAdmin = userGroups.includes('mtkd-admin');

    const getRankName = (rankId) => {
        const rank = ranks.find(r => r.rank_id === rankId);
        console.log('Looking up rank:', rankId, 'Found:', rank);
        return rank ? rank.rank : rankId;
    };

    if (loading) {
        return (
            <div className="container">
                <Navbar groups={userGroups}/>
                <div className="card">
                    <div className="card-body">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <Navbar groups={userGroups}/>
            <div className="card">
                <div className="card-body">
                    <h2 className="card-title">Classes</h2>
                    {isAdmin && (
                        <div className="mb-3">
                            <h4>Add New Class</h4>
                            <div className="row">
                                <div className="col-md-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Class Name"
                                        value={newClass.class_name}
                                        onChange={(e) => setNewClass({ ...newClass, class_name: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-control"
                                        value={newClass.day_of_week}
                                        onChange={(e) => setNewClass({ ...newClass, day_of_week: parseInt(e.target.value) })}
                                    >
                                        <option value={1}>Monday</option>
                                        <option value={2}>Tuesday</option>
                                        <option value={3}>Wednesday</option>
                                        <option value={4}>Thursday</option>
                                        <option value={5}>Friday</option>
                                        <option value={6}>Saturday</option>
                                        <option value={7}>Sunday</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={newClass.start_time}
                                        onChange={(e) => setNewClass({ ...newClass, start_time: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-1">
                                    <select
                                        className="form-control"
                                        value={newClass.min_rank}
                                        onChange={(e) => setNewClass({ ...newClass, min_rank: parseInt(e.target.value) })}
                                    >
                                        {ranks.map(rank => (
                                            <option key={rank.rank_id} value={rank.rank_id}>
                                                {rank.rank}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-1">
                                    <select
                                        className="form-control"
                                        value={newClass.max_rank}
                                        onChange={(e) => setNewClass({ ...newClass, max_rank: parseInt(e.target.value) })}
                                    >
                                        {ranks.map(rank => (
                                            <option key={rank.rank_id} value={rank.rank_id}>
                                                {rank.rank}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-1">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Min Age"
                                        value={newClass.min_age}
                                        onChange={(e) => setNewClass({ ...newClass, min_age: parseInt(e.target.value) })}
                                    />
                                </div>
                                <div className="col-md-1">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Max Age"
                                        value={newClass.max_age}
                                        onChange={(e) => setNewClass({ ...newClass, max_age: parseInt(e.target.value) })}
                                    />
                                </div>
                                <div className="col-md-1">
                                    <button className="btn btn-primary" onClick={handleAdd}>Add</button>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Class Name</th>
                                    <th>Day</th>
                                    <th>Time</th>
                                    <th>Min Rank</th>
                                    <th>Max Rank</th>
                                    <th>Min Age</th>
                                    <th>Max Age</th>
                                    <th>Active</th>
                                    {isAdmin && <th>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {classes.map((classItem) => (
                                    <tr key={classItem.class_id}>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editingClass.class_name}
                                                    onChange={(e) => setEditingClass({ ...editingClass, class_name: e.target.value })}
                                                />
                                            ) : (
                                                classItem.class_name
                                            )}
                                        </td>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <select
                                                    className="form-control"
                                                    value={editingClass.day_of_week}
                                                    onChange={(e) => setEditingClass({ ...editingClass, day_of_week: parseInt(e.target.value) })}
                                                >
                                                    <option value={1}>Monday</option>
                                                    <option value={2}>Tuesday</option>
                                                    <option value={3}>Wednesday</option>
                                                    <option value={4}>Thursday</option>
                                                    <option value={5}>Friday</option>
                                                    <option value={6}>Saturday</option>
                                                    <option value={7}>Sunday</option>
                                                </select>
                                            ) : (
                                                ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][classItem.day_of_week - 1]
                                            )}
                                        </td>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    value={editingClass.start_time}
                                                    onChange={(e) => setEditingClass({ ...editingClass, start_time: e.target.value })}
                                                />
                                            ) : (
                                                classItem.start_time
                                            )}
                                        </td>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <select
                                                    className="form-control"
                                                    value={getRankName(editingClass.min_rank)}
                                                    onChange={(e) => setEditingClass({ ...editingClass, min_rank: parseInt(e.target.value) })}
                                                >
                                                    {ranks.map(rank => (
                                                        <option key={rank.rank_id} value={rank.rank_id}>
                                                            {rank.rank}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                getRankName(parseInt(classItem.min_rank))
                                            )}
                                        </td>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <select
                                                    className="form-control"
                                                    value={getRankName(editingClass.max_rank)}
                                                    onChange={(e) => setEditingClass({ ...editingClass, max_rank: parseInt(e.target.value) })}
                                                >
                                                    {ranks.map(rank => (
                                                        <option key={rank.rank_id} value={rank.rank_id}>
                                                            {rank.rank}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                getRankName(parseInt(classItem.max_rank))
                                            )}
                                        </td>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={editingClass.min_age}
                                                    onChange={(e) => setEditingClass({ ...editingClass, min_age: parseInt(e.target.value) })}
                                                />
                                            ) : (
                                                classItem.min_age
                                            )}
                                        </td>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={editingClass.max_age}
                                                    onChange={(e) => setEditingClass({ ...editingClass, max_age: parseInt(e.target.value) })}
                                                />
                                            ) : (
                                                classItem.max_age
                                            )}
                                        </td>
                                        <td>
                                            {editingClass?.class_id === classItem.class_id ? (
                                                <input
                                                    type="checkbox"
                                                    checked={editingClass.active}
                                                    onChange={(e) => setEditingClass({ ...editingClass, active: e.target.checked })}
                                                />
                                            ) : (
                                                <input type="checkbox" checked={classItem.active} disabled />
                                            )}
                                        </td>
                                        {isAdmin && (
                                            <td>
                                                {editingClass?.class_id === classItem.class_id ? (
                                                    <button className="btn btn-success btn-sm" onClick={handleSave}>Save</button>
                                                ) : (
                                                    <button className="btn btn-primary btn-sm" onClick={() => handleEdit(classItem)}>Edit</button>
                                                )}
                                                <button className="btn btn-danger btn-sm ms-2" onClick={() => handleDelete(classItem.class_id)}>Delete</button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Class; 